import React, { useState } from 'react';
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useActiveVehicle } from '../../context/ActiveVehicleProvider';
import { useTicketTypeMeta } from '../../context/TicketTerminologyProvider';
import { TicketType } from '../../types/ticket';
import TicketRecord from '../ticketRecord/TicketRecord';

const CompactViolationHistory: React.FC = () => {
	// Window-only by design (the full/greyed history lives in the action drawer).
	const { inWindowTickets: tickets, currentSearch } = useActiveVehicle();
	const getTicketTypeMeta = useTicketTypeMeta();
	const [expanded, setExpanded] = useState(false);
	const parkingLocation = currentSearch?.parkingLocation;
	if (!parkingLocation) return null;

	const count = tickets.length;
	const totalLabel = `${count} prior ${count === 1 ? 'record' : 'records'}`;

	const breakdown = tickets.reduce<Record<string, number>>((acc, t) => {
		acc[t.ticketType] = (acc[t.ticketType] ?? 0) + 1;
		return acc;
	}, {});
	const breakdownParts = (Object.keys(breakdown) as TicketType[])
		.map((type) => {
			const n = breakdown[type];
			const meta = getTicketTypeMeta(type);
			const label =
				n === 1 ? meta.shortLabel.toLowerCase() : meta.pluralLabel;
			return `${n} ${label}`;
		})
		.join(' · ');

	const canToggle = count > 0;

	return (
		<Box
			sx={{
				border: 1,
				borderColor: 'divider',
				borderRadius: 2,
				width: '100%',
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				spacing={1}
				onClick={canToggle ? () => setExpanded((v) => !v) : undefined}
				sx={{
					p: 1,
					cursor: canToggle ? 'pointer' : 'default',
					'&:hover': canToggle
						? { backgroundColor: 'action.hover' }
						: undefined,
					borderRadius: 2,
				}}
			>
				<HistoryIcon fontSize="small" />
				<Box sx={{ flexGrow: 1, minWidth: 0 }}>
					<Typography variant="body2" fontWeight={400}>
						{totalLabel}
					</Typography>
					{breakdownParts && (
						<Typography
							variant="caption"
							color="text.secondary"
							fontWeight={300}
						>
							{breakdownParts}
						</Typography>
					)}
				</Box>
				{canToggle && (
					<IconButton
						size="small"
						aria-label={expanded ? 'Hide history' : 'Show history'}
					>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				)}
			</Stack>
			<Collapse in={expanded} unmountOnExit>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 1,
						p: 1,
						pt: 0,
					}}
				>
					{tickets.map((ticket) => (
						<TicketRecord
							key={ticket.ticketNumber}
							ticket={ticket}
							parkingLocation={parkingLocation}
						/>
					))}
				</Box>
			</Collapse>
		</Box>
	);
};

export default CompactViolationHistory;
